// Admin only, read-only: what the nightly draft clean-up would remove and what it keeps, counts by age
// (GET /api/portal/drafts-cleanup). Never shows a name. Switching the clean-up on is an App Setting in Azure
// (DRAFT_RETENTION_MODE = delete), not a button here.
import { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2 } from "lucide-react";

import { API, SENDING_ON } from "../lib/portalMode";

const fmt = (n) => Number(n || 0).toLocaleString("en-AU");

export function dayInWords(iso) {
  if (!iso) return "";
  const t = Date.parse(`${String(iso).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(t)) return "";
  return new Date(t).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric", timeZone: "Australia/Sydney" });
}

export function modeLine(mode, days) {
  if (mode === "delete") return `The clean-up is ON: each night it removes drafts with no save for ${days} days.`;
  if (mode === "off") return "The clean-up is switched off: it doesn't run at all.";
  return `The clean-up is counting only (dry run). Nothing has been removed. Once it's switched on, each night it removes drafts with no save for ${days} days.`;
}

function Rows({ rows, testid }) {
  return (
    <table className="w-full text-sm" data-testid={testid}>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className="border-b border-slate-100 last:border-0">
            <td className="py-1.5 text-slate-600">{r.label}</td>
            <td className="py-1.5 text-right font-medium text-slate-800 tabular-nums">{fmt(r.count)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function DraftCleanupPanel({ authToken, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!SENDING_ON) { setError("Preview: this only works on the live portal."); return; }
    axios.get(`${API}/portal/drafts-cleanup`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => setData(r.data))
      .catch(() => setError("Couldn't load the draft clean-up counts. Please try again."));
  }, [authToken]);

  const days = (data && data.retention_days) || 30;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" data-testid="draft-cleanup-modal">
      <div className="bg-white rounded-xl max-w-xl w-full my-6 shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">Draft clean-up</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="draft-cleanup-close-btn" aria-label="Close"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-6">
          {error && <p className="text-sm text-red-600" data-testid="draft-cleanup-error">{error}</p>}
          {!data && !error && (
            <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Counting drafts...</div>
          )}
          {data && (
            <>
              <p className="text-sm text-slate-600" data-testid="draft-cleanup-mode">{modeLine(data.mode, days)}</p>

              <div>
                <div className="text-sm font-semibold text-slate-800 mb-1" data-testid="draft-cleanup-remove-total">
                  Would remove: {fmt(data.would_remove_total)} drafts
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Forms someone started and hasn't saved for more than {days} days, by how long since the last save.
                  {data.oldest_last_save ? ` The oldest was last saved on ${dayInWords(data.oldest_last_save)}.` : ""}
                </p>
                <Rows rows={data.would_remove} testid="draft-cleanup-remove" />
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-800 mb-1" data-testid="draft-cleanup-keep-total">
                  Keeps: {fmt(data.keeps_drafts_total)} drafts
                </div>
                <p className="text-xs text-slate-500 mb-2">Any draft saved in the last {days} days is never removed.</p>
                <Rows rows={data.keeps} testid="draft-cleanup-keep" />
              </div>

              <p className="text-sm text-slate-600" data-testid="draft-cleanup-completed">
                Completed submissions are never touched: {fmt(data.completed_never_touched)}.
              </p>

              {data.recent_runs && data.recent_runs.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-1">Recent nightly runs</div>
                  <ul className="text-xs text-slate-500 space-y-0.5" data-testid="draft-cleanup-runs">
                    {data.recent_runs.map((r) => (
                      <li key={r.at}>
                        {dayInWords(r.at)}: {r.mode === "delete" ? `removed ${fmt(r.deleted)}` : `would have removed ${fmt(r.due)}`} of {fmt(r.drafts_checked)} drafts
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
